// src/components/stoc/StockCurrentList.jsx
import StockCurrentRow from "./StockCurrentRow";
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Form,
  InputGroup,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { AsyncPaginate } from "react-select-async-paginate";
import { components } from "react-select";
import { authFetch } from "../utils/authFetch";

const LIMIT = 20;

function SortHeader({ label, col, sortBy, sortDir, onSort, className }) {
  const active = sortBy === col;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th
      role="button"
      className={`user-select-none ${className || ""}`}
      onClick={() => onSort(col)}
      title={`Sortează după ${label}`}
    >
      <span className="me-1">{label}</span>
      <span style={{ opacity: 0.6, fontSize: 12 }}>{arrow}</span>
    </th>
  );
}

// react-select highlighted option
const HighlightedOption = (props) => {
  const q = (props.selectProps.inputValue || "").trim();
  const label = props.data.label || "";
  const parts = q ? label.split(new RegExp(`(${q})`, "i")) : [label];
  return (
    <components.Option {...props}>
      {parts.map((p, i) =>
        q && p.toLowerCase() === q.toLowerCase() ? (
          <mark key={i}>{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </components.Option>
  );
};

// generic paged loader
const loadPaged = async (url, q, page, limit) => {
  const skip = (page - 1) * limit;
  const sep = url.includes("?") ? "&" : "?";
  const full = `${url}${sep}search=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`;
  const data = await authFetch(full);

  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : [];
  const total =
    typeof data?.total === "number"
      ? data.total
      : typeof data?.count === "number"
        ? data.count
        : undefined;

  const hasMore =
    typeof total === "number"
      ? skip + items.length < total
      : items.length === limit;
  return { items, hasMore };
};

export default function StockCurrentList() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filtre
  const [q, setQ] = useState("");

  // gestiune select (AsyncPaginate ca la NIR)
  const [selGestiune, setSelGestiune] = useState(null);
  const [gestiuneId, setGestiuneId] = useState(0);

  // sort
  const [sortBy, setSortBy] = useState("cod");
  const [sortDir, setSortDir] = useState("asc");

  // paginate
  const [currentPage, setCurrentPage] = useState(0); // 0-based
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const onSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  // loader gestiuni (identic ca în NirForm)
  const loadGestiuneOptions = async (inputValue, _l, additional) => {
    const page = additional?.page ?? 1;
    const qq = (inputValue || "").trim();
    const bases = ["/bxgestiune/"];

    for (const base of bases) {
      try {
        const { items: rows, hasMore } = await loadPaged(base, qq, page, LIMIT);
        const options = rows.map((g) => ({
          value: g.id,
          label:
            `${g.bxcod ?? g.cod ?? g.code ?? g.id} — ${g.bxnume ?? g.denumire ?? g.bxdenumire ?? ""}`.trim(),
        }));
        return { options, hasMore, additional: { page: page + 1 } };
      } catch {}
    }
    return { options: [], hasMore: false, additional: { page } };
  };

  const handleGestiuneChange = (opt) => {
    setSelGestiune(opt);
    setGestiuneId(opt ? Number(opt.value) : 0);
  };

  // (opțional) reține ultima gestiune aleasă
  useEffect(() => {
    if (gestiuneId) localStorage.setItem("gestiune_id", String(gestiuneId));
  }, [gestiuneId]);

  // preload gestiune din localStorage (opțional)
  useEffect(() => {
    const saved = Number(localStorage.getItem("gestiune_id") || 0);
    if (!saved) return;

    (async () => {
      const candidates = [`/bxgestiune/${saved}`];
      for (const url of candidates) {
        try {
          const g = await authFetch(url);
          setSelGestiune({
            value: g.id,
            label:
              `${g.bxcod ?? g.cod ?? g.code ?? g.id} — ${g.bxnume ?? g.denumire ?? g.bxdenumire ?? ""}`.trim(),
          });
          setGestiuneId(g.id);
          break;
        } catch {}
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset pagina la schimbarea filtrelor/sortării/mărimii paginii/gestiunii
  useEffect(() => {
    setCurrentPage(0);
  }, [q, gestiuneId, sortBy, sortDir, itemsPerPage]);

  const fetchList = async () => {
    // fără gestiune nu are sens
    if (!gestiuneId) {
      setItems([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const skip = currentPage * itemsPerPage;

      const usp = new URLSearchParams();
      usp.set("gestiune_id", String(gestiuneId));
      if (q?.trim()) usp.set("q", q.trim());
      usp.set("sort_by", sortBy);
      usp.set("sort_dir", sortDir);
      usp.set("skip", String(skip));
      usp.set("limit", String(itemsPerPage));

      const path = `/stock/current?${usp.toString()}`;
      const data = await authFetch(path);

      // așteptat: { total, items } (ca la IntrareList)
      if (data && Array.isArray(data.items) && typeof data.total === "number") {
        setItems(data.items);
        setTotalCount(data.total);
      } else if (Array.isArray(data)) {
        // fallback dacă endpointul întoarce listă simplă
        setItems(data);
        setTotalCount(data.length);
      } else {
        setItems([]);
        setTotalCount(0);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  // inițial + când dependențele paginate/filtre/sort se schimbă
  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, q, gestiuneId, sortBy, sortDir]);

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const from = totalCount ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalCount, (currentPage + 1) * itemsPerPage);

  const canSearch = useMemo(() => Boolean(gestiuneId), [gestiuneId]);

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h3 className="mb-0">Stoc curent</h3>
        </Col>
        <Col xs="auto" className="text-muted">
          {totalCount ? `Afișez ${from}-${to} din ${totalCount}` : "—"}
        </Col>
      </Row>

      <Row className="mb-3 g-2">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Gestiune</Form.Label>
            <AsyncPaginate
              value={selGestiune}
              loadOptions={loadGestiuneOptions}
              onChange={handleGestiuneChange}
              isClearable
              placeholder="Caută gestiune..."
              debounceTimeout={300}
              cacheOptions
              defaultOptions
              openMenuOnFocus
              additional={{ page: 1 }}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              components={{ Option: HighlightedOption }}
            />
          </Form.Group>
        </Col>

        <Col md={5}>
          <Form.Group>
            <Form.Label>Căutare</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder={
                  canSearch
                    ? "Caută după cod sau denumire"
                    : "Selectează întâi o gestiune"
                }
                value={q}
                disabled={!canSearch}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchList();
                }}
              />
              <Button
                variant="outline-primary"
                disabled={!canSearch}
                onClick={() => fetchList()}
              >
                Caută
              </Button>
              <Button
                variant="outline-secondary"
                disabled={!canSearch}
                onClick={() => setQ("")}
              >
                Golește
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>

        <Col
          md={3}
          className="d-flex justify-content-end align-items-end gap-2"
        >
          <Form.Select
            size="sm"
            style={{ width: 120 }}
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            title="Rânduri pe pagină"
            disabled={!canSearch}
          >
            {[10, 20, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n}/pag.
              </option>
            ))}
          </Form.Select>

          <Button
            variant="outline-secondary"
            disabled={!canSearch}
            onClick={() => {
              setQ("");
              setSortBy("cod");
              setSortDir("asc");
            }}
            title="Resetează filtrele și sortarea"
          >
            Resetează
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th aria-label="Detalii stoc" style={{width: 40}} />
                <SortHeader
                  label="Cod"
                  col="cod"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Denumire"
                  col="denumire"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Preț"
                  col="pret"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  className="text-end"
                />
                <SortHeader
                  label="Cantitate"
                  col="cantitate"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  className="text-end"
                />
              </tr>
            </thead>

            <tbody>
              {items.map((it, idx) => (
                <StockCurrentRow key={`${gestiuneId}-${it.bxstocin_id}-${it.pret_magazie}-${idx}`}
                  item={it} warehouseId={gestiuneId} />
              ))}

              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    {gestiuneId
                      ? "Nu există înregistrări."
                      : "Selectează o gestiune."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            renderOnZeroPageCount={null}
          />
        </>
      )}
    </Container>
  );
}
